import { useAngularServices } from '@/react/components/ReactAngularContext'
import { CurrentUserType } from '@/react/types/documentation'
import { css } from '@emotion/react'
import { useEffect, useState } from 'react'
import AssignInternalModal from './AssignInternalModal'
import AssignExternalModal from './AssignExternalModal'
import { Answer } from '@/react/types/observations'
import { BinDeleteIcon } from '@/react/componentAssets/BinDeleteIcon'

const InternalExternalSection = ({
  answerId,
  isVisible = true,
  onAnswerUpdate,
}: {
  answerId: number
  isVisible?: boolean
  onAnswerUpdate?: () => void
}) => {
  const { CurrentUser, Api } = useAngularServices()
  const variableColor = (CurrentUser as CurrentUserType).getClientSettings()
    .web_primary_color

  const [isAssignInternalModalOpen, setIsAssignInternalModalOpen] =
    useState(false)
  const [isAssignExternalModalOpen, setIsAssignExternalModalOpen] =
    useState(false)
  const [answer, setAnswer] = useState<Answer | null>(null)
  const [dataFetched, setDataFetched] = useState(false)
  const [loading, setLoading] = useState(true)
  const internalExternalSectionStyles = getInternalExternalSectionStyles(
    variableColor,
    loading,
  )

  const fetchAnswers = async () => {
    try {
      const result = await Api.get(`answers/${answerId}`)
      if (result && typeof result === 'object' && 'data' in result) {
        setAnswer(result.data as Answer)
      }
    } catch (error) {
      console.error('Error fetching internal assignees:', error)
      setAnswer(null)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (!isVisible || !answerId || dataFetched) {
        return
      }

      try {
        setLoading(true)
        await Promise.all([fetchAnswers()])

        setDataFetched(true)
      } catch (error) {
        console.error('Error fetching assignees:', error)
      } finally {
        setLoading(false)
      }
    }

    void fetchData()
  }, [isVisible, answerId, Api, dataFetched])

  useEffect(() => {
    if (answerId) {
      setDataFetched(false)
    }
  }, [answerId])

  if (!isVisible) {
    return null
  }

  const handleDeleteInternalAssignee = async (id: number) => {
    try {
      await Api.patch(`answers_internal_assignees/${id}`, {
        deleted: true,
      })

      await fetchAnswers()
    } catch (error) {
      console.error('Error deleting internal assignee:', error)
    }
  }

  const handleDeleteExternalAssignee = async (id: number) => {
    try {
      await Api.patch(`answers_external_assignees/${id}`, {
        deleted: true,
      })

      await fetchAnswers()
    } catch (error) {
      console.error('Error deleting external assignee:', error)
    }
  }

  return (
    <div css={internalExternalSectionStyles}>
      <div className="internal-external-section-item">
        <div className="internal-external-section-item-header">
          <p>Internal Assignee:</p>
          <span onClick={() => setIsAssignInternalModalOpen(true)}>
            + Assign Internal
          </span>
        </div>
        {loading ? (
          <div className="loader-container">
            <div css={loaderStyles}>
              <div className="loader-spinner"></div>
              <span>Loading internal assignees...</span>
            </div>
          </div>
        ) : (
          <div className="internal-external-section-item-assignees">
            {answer?.internal_assignees
              .filter((assignee) => assignee.deleted === false)
              .sort((a, b) => {
                const nameA = `${a?.full_assignee?.first_name || ''} ${
                  a?.full_assignee?.last_name || ''
                }`
                  .trim()
                  .toLowerCase()
                const nameB = `${b?.full_assignee?.first_name || ''} ${
                  b?.full_assignee?.last_name || ''
                }`
                  .trim()
                  .toLowerCase()
                return nameA.localeCompare(nameB)
              })
              .map((assignee) => (
                <div
                  key={assignee.id}
                  className="internal-external-section-item-assignee"
                >
                  <BinDeleteIcon
                    color={variableColor}
                    onClick={() =>
                      void handleDeleteInternalAssignee(assignee.id)
                    }
                    width="17px"
                    height="17px"
                  />
                  <p>
                    {assignee?.full_assignee?.first_name}{' '}
                    {assignee?.full_assignee?.last_name}
                  </p>
                </div>
              ))}
          </div>
        )}
      </div>

      <div className="internal-external-section-item">
        <div className="internal-external-section-item-header">
          <p>External Assignee:</p>
          <span onClick={() => setIsAssignExternalModalOpen(true)}>
            + Assign External
          </span>
        </div>
        {loading ? (
          <div className="loader-container">
            <div css={loaderStyles}>
              <div className="loader-spinner"></div>
              <span>Loading external assignees...</span>
            </div>
          </div>
        ) : (
          <div className="internal-external-section-item-assignees">
            {answer?.external_assignees
              .filter((assignee) => assignee.deleted === false)
              .sort((a, b) => {
                const emailA = (a?.external_contact?.email || '').toLowerCase()
                const emailB = (b?.external_contact?.email || '').toLowerCase()
                return emailA.localeCompare(emailB)
              })
              .map((assignee) => (
                <div
                  key={assignee.id}
                  className="internal-external-section-item-assignee"
                >
                  <BinDeleteIcon
                    color={variableColor}
                    onClick={() =>
                      void handleDeleteExternalAssignee(assignee.id)
                    }
                    width="17px"
                    height="17px"
                  />
                  <p>{assignee?.external_contact?.email}</p>
                </div>
              ))}
          </div>
        )}
      </div>

      {isAssignInternalModalOpen && (
        <AssignInternalModal
          title="Assign Internal Assignee"
          handleClose={() => setIsAssignInternalModalOpen(false)}
          answerId={answerId}
          observationId={answer?.observation}
          questionId={answer?.question}
          existingAssignees={answer?.internal_assignees || []}
          onAssignmentComplete={() => {
            void fetchAnswers()
            onAnswerUpdate?.()
          }}
        />
      )}
      {isAssignExternalModalOpen && (
        <AssignExternalModal
          title="Assign External Assignee"
          handleClose={() => setIsAssignExternalModalOpen(false)}
          answerId={answerId}
          observationId={answer?.observation}
          questionId={answer?.question}
          externalAssignee={answer?.external_assignees || []}
          fetchAnswers={() => {
            void fetchAnswers()
            onAnswerUpdate?.()
          }}
        />
      )}
    </div>
  )
}

const loaderStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  padding: '20px',
  color: '#666',
  fontSize: '14px',

  '.loader-spinner': {
    width: '20px',
    height: '20px',
    border: '2px solid #f3f3f3',
    borderTop: '2px solid #3498db',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },

  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
})

const getInternalExternalSectionStyles = (
  variableColor: string,
  loading: boolean,
) =>
  css({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    marginBottom: '28px',
    gap: '16px',

    '.loader-container': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
    },

    '.internal-external-section-item': {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',

      '.internal-external-section-item-header': {
        display: 'flex',
        gap: '16px',

        p: {
          fontSize: '13px',
          color: '#676a6c',
          fontWeight: 700,
          letterSpacing: '0.12px',
          margin: 0,
        },

        span: {
          fontSize: '13px',
          color: variableColor,
          fontWeight: 400,
          letterSpacing: '0.12px',
          cursor: 'pointer',
          margin: 0,
        },
      },

      '.internal-external-section-item-assignees': {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        alignItems: 'center',
        gap: '8px',

        '.internal-external-section-item-assignee': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '5px',
          minWidth: 0,

          p: {
            color: '#767676',
            fontSize: '14px',
            fontWeight: 400,
            letterSpacing: '0.12px',
            margin: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
          },

          img: {
            width: '16px',
            height: '16px',
            cursor: 'pointer',
            flexShrink: 0,
          },
        },
      },
    },
  })

export default InternalExternalSection
